import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Send } from "lucide-react";

export const Route = createFileRoute("/admin/telegram")({ component: TelegramAdmin });

function normalize(u: string) {
  return (u || "").trim().replace(/^@/, "").replace(/^https?:\/\/t\.me\//i, "").replace(/\/+$/, "");
}

function TelegramAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin_settings_telegram"],
    queryFn: async () => {
      const { data } = await supabase.from("app_settings").select("value").eq("key", "telegram").maybeSingle();
      return (data?.value ?? { admin_username: "", channel_username: "" }) as { admin_username: string; channel_username: string };
    },
  });

  const [admin, setAdmin] = useState("");
  const [channel, setChannel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    setAdmin(data.admin_username ?? "");
    setChannel(data.channel_username ?? "");
  }, [data]);

  async function save() {
    setSaving(true);
    const value = { admin_username: normalize(admin), channel_username: normalize(channel) };
    const { error } = await supabase.from("app_settings").upsert({ key: "telegram", value, updated_at: new Date().toISOString() });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Telegram settings saved");
    qc.invalidateQueries({ queryKey: ["app_settings"] });
    qc.invalidateQueries({ queryKey: ["admin_settings_telegram"] });
  }

  const adminPreview = normalize(admin);
  const channelPreview = normalize(channel);

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-24">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#229ED9]/15 text-[#229ED9]">
          <Send className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-black">Telegram Settings</h1>
          <p className="text-xs text-muted-foreground">Shown to customers on the Help page</p>
        </div>
      </div>

      <div className="space-y-4 rounded-2xl bg-card p-4 shadow-card">
        <div className="space-y-1.5">
          <Label className="text-xs">Admin Telegram Username</Label>
          <Input value={admin} onChange={(e) => setAdmin(e.target.value)} placeholder="e.g. brothersclub_admin" />
          {adminPreview && <p className="text-[11px] text-muted-foreground">Link: https://t.me/{adminPreview}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Telegram Channel Username</Label>
          <Input value={channel} onChange={(e) => setChannel(e.target.value)} placeholder="e.g. brothersclub_official" />
          {channelPreview && <p className="text-[11px] text-muted-foreground">Link: https://t.me/{channelPreview}</p>}
        </div>
        <p className="text-[11px] text-muted-foreground">Enter usernames without the @ symbol. Leave a field empty to hide that option from customers.</p>
        <Button onClick={save} disabled={saving} className="gradient-primary">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save
        </Button>
      </div>
    </div>
  );
}
